import { useEffect, useState, useMemo } from "react";
import { useQuery } from "@apollo/react-hooks";
import { UI_COMPONENT } from "@src/queries";
import { uiComponent } from "@src/queries/__generated__/uiComponent";
import { uiComponentVariables } from "@src/core/config";
import { PRODUCT_LIST } from "@src/components/ProductPage/ProductList/queries";
import { products } from "@src/components/ProductPage/ProductList/__generated__/products";
import { getGraphqlIdFromDBId, getDBIdFromGraphqlId } from "@utils";
import {
    setDynamicInvisibleBrandIds,
    setDynamicInvisibleIds,
    INVISIBLE_PRODUCT_IDS,
    INVISIBLE_BRAND_IDS,
} from "@src/utils/constants";

const PRODUCT_ID_STORAGE_KEY = "invisibleProductIds";
const PRODUCT_EXPIRE_KEY = "invisibleProductIdsExpires";
const BRAND_ID_STORAGE_KEY = "invisibleBrandIds";
const BRAND_EXPIRE_KEY = "invisibleBrandIdsExpires";
const CACHE_TTL = 60 * 60 * 1000; // 1시간

function isCacheValid(key: string): boolean {
    const expires = localStorage.getItem(key);
    if (!expires) return false;
    const expiry = parseInt(expires, 10);
    return !isNaN(expiry) && expiry > Date.now();
}

function getCachedArray(key: string): string[] {
    try {
        const cached = localStorage.getItem(key);
        if (!cached) return [];
        return JSON.parse(cached);
    } catch {
        return [];
    }
}

function setCachedArray(key: string, expireKey: string, ids: string[]) {
    localStorage.setItem(key, JSON.stringify(ids));
    localStorage.setItem(expireKey, String(Date.now() + CACHE_TTL));
}

export function useInvisibleProductIds(): boolean {
    const productCached = isCacheValid(PRODUCT_EXPIRE_KEY);
    const brandCached = isCacheValid(BRAND_EXPIRE_KEY);
    const [ready, setReady] = useState<boolean>(productCached && brandCached);

    // 1. 캐시 유효할 경우 메모리에 로드
    useEffect(() => {
        if (productCached) {
            const merged = [...new Set([...INVISIBLE_PRODUCT_IDS, ...getCachedArray(PRODUCT_ID_STORAGE_KEY)])];
            setDynamicInvisibleIds(merged);
        }
        if (brandCached) {
            const merged = [...new Set([...INVISIBLE_BRAND_IDS, ...getCachedArray(BRAND_ID_STORAGE_KEY)])];
            setDynamicInvisibleBrandIds(merged);
        }
        if (productCached && brandCached) {
            setReady(true);
        }
    }, [productCached, brandCached]);

    // 2. UI 쿼리로부터 숨길 브랜드 ID 리스트 가져오기
    const { data: uiData, error: uiError } = useQuery<uiComponent>(UI_COMPONENT, {
        variables: uiComponentVariables.invisibleBrand,
        fetchPolicy: "network-only",
        skip: productCached && brandCached,
    });

    const brandIds = useMemo<string[]>(() => {
        const raw = uiData?.uicomponent?.items?.[0]?.body;
        if (!raw) return [];
        return raw
            .split(",")
            .map((s) => s.trim())
            .filter((s) => !isNaN(Number(s)))
            .map((s) => getGraphqlIdFromDBId(s, "Brand"));
    }, [uiData]);

    const brandDBIds = useMemo(() => brandIds.map((id) => getDBIdFromGraphqlId(id, "Brand").toString()), [brandIds]);

    // 3. 해당 브랜드들의 상품 리스트 조회 (숨김 상품 추출용)
    const { data: prodData, error: prodError } = useQuery<products>(PRODUCT_LIST, {
        variables: { brands: brandIds, first: 100 },
        fetchPolicy: "network-only",
        skip: (productCached && brandCached) || brandIds.length === 0,
    });

    // 4. 데이터 받아오면 캐시 + 메모리 세팅
    useEffect(() => {
        if (productCached && brandCached) return;

        // brand만 먼저 처리
        const mergedBrandIds = [...new Set([...INVISIBLE_BRAND_IDS, ...brandDBIds])];
        setDynamicInvisibleBrandIds(mergedBrandIds);
        setCachedArray(BRAND_ID_STORAGE_KEY, BRAND_EXPIRE_KEY, mergedBrandIds);

        if (prodError || uiError) {
            console.warn("InvisibleProductIds: GraphQL Error", { prodError, uiError });
            setReady(true);
            return;
        }

        const edges = prodData?.products?.edges;
        if (!edges) {
            setReady(true);
            return;
        }

        const dynamicIds = edges.map((edge) => String(getDBIdFromGraphqlId(edge.node.id, "Product")));
        const mergedIds = [...new Set([...INVISIBLE_PRODUCT_IDS, ...dynamicIds])];

        setDynamicInvisibleIds(mergedIds);
        setCachedArray(PRODUCT_ID_STORAGE_KEY, PRODUCT_EXPIRE_KEY, mergedIds);
        setReady(true);
    }, [productCached, brandCached, prodData, prodError, uiError, brandIds]);

    return ready;
}
